import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import {
  Calculator,
  Plus,
  Search,
  TrendingUp,
  DollarSign,
  Calendar,
  Send,
  Eye,
  Edit,
  Copy,
  Loader2,
  FileText,
  Building2,
  BarChart3,
  ArrowUpRight,
  Percent,
  Clock,
  Users,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

const STATUS_COLORS = {
  draft: 'bg-slate-500',
  sent: 'bg-blue-500',
  negotiating: 'bg-amber-500',
  accepted: 'bg-emerald-500',
  rejected: 'bg-red-500',
};

export default function ProposalBuilderPage() {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [malls, setMalls] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [newProposalOpen, setNewProposalOpen] = useState(false);
  const [viewProposalOpen, setViewProposalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [proposalForm, setProposalForm] = useState({
    lead_id: searchParams.get('lead_id') || '',
    brand_name: '',
    contact_name: '',
    contact_email: '',
    carpet_area_sqft: 2000,
    rent_per_sqft: 300,
    cam_per_sqft: 50,
    revenue_share_percent: 0,
    security_deposit_months: 6,
    lease_term_years: 5,
    fit_out_period_days: 90,
    rent_free_period_days: 30,
    escalation_percent: 5,
    notes: '',
  });

  // Live calculation preview
  const [previewCalc, setPreviewCalc] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    calculatePreview();
  }, [proposalForm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [proposalsRes, leadsRes, mallsRes] = await Promise.all([
        api.get('/proposals/'),
        api.get('/leasing/'),
        api.get('/malls/'),
      ]);
      setProposals(proposalsRes.data);
      setLeads(leadsRes.data);
      setMalls(mallsRes.data);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  const calculatePreview = () => {
    const { carpet_area_sqft, rent_per_sqft, cam_per_sqft, revenue_share_percent, security_deposit_months, lease_term_years, escalation_percent } = proposalForm;
    
    const monthlyRent = carpet_area_sqft * rent_per_sqft;
    const monthlyCam = carpet_area_sqft * cam_per_sqft;
    const annualRent = monthlyRent * 12;
    const securityDeposit = monthlyRent * security_deposit_months;
    
    let currentRent = annualRent;
    let totalRevenue = 0;
    const projections = [];
    
    for (let year = 1; year <= lease_term_years; year++) {
      if (year > 1) {
        currentRent = currentRent * (1 + escalation_percent / 100);
      }
      const yearCam = monthlyCam * 12;
      const yearRevShare = currentRent * (revenue_share_percent / 100);
      const yearTotal = currentRent + yearCam + yearRevShare;
      totalRevenue += yearTotal;
      
      projections.push({
        year: `Year ${year}`,
        rent: Math.round(currentRent),
        cam: Math.round(yearCam),
        revShare: Math.round(yearRevShare),
        total: Math.round(yearTotal),
      });
    }
    
    setPreviewCalc({
      monthlyRent: Math.round(monthlyRent),
      monthlyCam: Math.round(monthlyCam),
      annualRent: Math.round(annualRent),
      securityDeposit: Math.round(securityDeposit),
      totalContractValue: Math.round(totalRevenue),
      avgAnnualRevenue: Math.round(totalRevenue / lease_term_years),
      projections,
    });
  };

  const handleLeadSelect = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      setProposalForm(f => ({
        ...f,
        lead_id: leadId,
        brand_name: lead.brand_name || '',
        contact_name: lead.contact_name || '',
        contact_email: lead.contact_email || '',
        carpet_area_sqft: lead.required_area_sqft || 2000,
      }));
    }
  };

  const handleCreateProposal = async () => {
    if (!proposalForm.brand_name || !proposalForm.contact_email) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      setSubmitting(true);
      const res = await api.post('/proposals/', proposalForm);
      toast.success('Proposal created successfully');
      setNewProposalOpen(false);
      setProposalForm({
        lead_id: '',
        brand_name: '',
        contact_name: '',
        contact_email: '',
        carpet_area_sqft: 2000,
        rent_per_sqft: 300,
        cam_per_sqft: 50,
        revenue_share_percent: 0,
        security_deposit_months: 6,
        lease_term_years: 5,
        fit_out_period_days: 90,
        rent_free_period_days: 30,
        escalation_percent: 5,
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create proposal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendProposal = async (proposalId) => {
    try {
      await api.post(`/proposals/${proposalId}/send`);
      toast.success('Proposal sent successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to send proposal');
    }
  };

  const handleUpdateStatus = async (proposalId, newStatus) => {
    try {
      await api.put(`/proposals/${proposalId}`, { status: newStatus });
      toast.success('Proposal status updated');
      fetchData();
      if (selectedProposal?.id === proposalId) {
        setSelectedProposal(p => ({ ...p, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update proposal');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const filteredProposals = proposals.filter(p => {
    const matchesSearch = !searchQuery || 
      p.brand_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.contact_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totalPipelineValue = proposals.reduce((sum, p) => sum + (p.roi_metrics?.total_contract_value || 0), 0);
  const acceptedValue = proposals.filter(p => p.status === 'accepted').reduce((sum, p) => sum + (p.roi_metrics?.total_contract_value || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="proposals-loading">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
          <p className="text-muted-foreground">Loading proposals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="proposal-builder-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Proposal Builder</h1>
          <p className="text-muted-foreground">
            Create leasing proposals with ROI projections
          </p>
        </div>
        <Dialog open={newProposalOpen} onOpenChange={setNewProposalOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-proposal-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Proposal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Proposal</DialogTitle>
              <DialogDescription>
                Build a comprehensive leasing proposal with ROI projections
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-6 py-4">
              {/* Left Column - Inputs */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground">BASIC DETAILS</h3>
                
                {leads.length > 0 && (
                  <div className="space-y-2">
                    <Label>Link to Lead (optional)</Label>
                    <Select value={proposalForm.lead_id || "none"} onValueChange={(v) => handleLeadSelect(v === "none" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a lead to auto-fill" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No lead</SelectItem>
                        {leads.map(lead => (
                          <SelectItem key={lead.id} value={lead.id}>
                            {lead.brand_name} - {lead.contact_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Brand Name *</Label>
                    <Input
                      placeholder="e.g., Zara"
                      value={proposalForm.brand_name}
                      onChange={(e) => setProposalForm(f => ({ ...f, brand_name: e.target.value }))}
                      data-testid="brand-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Name *</Label>
                    <Input
                      placeholder="Primary contact"
                      value={proposalForm.contact_name}
                      onChange={(e) => setProposalForm(f => ({ ...f, contact_name: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Contact Email *</Label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={proposalForm.contact_email}
                    onChange={(e) => setProposalForm(f => ({ ...f, contact_email: e.target.value }))}
                    data-testid="email-input"
                  />
                </div>

                <h3 className="font-medium text-sm text-muted-foreground pt-4">FINANCIAL TERMS</h3>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Carpet Area (sq ft)</Label>
                    <span className="text-sm font-mono">{proposalForm.carpet_area_sqft.toLocaleString()}</span>
                  </div>
                  <Slider
                    value={[proposalForm.carpet_area_sqft]}
                    onValueChange={([v]) => setProposalForm(f => ({ ...f, carpet_area_sqft: v }))}
                    min={500}
                    max={20000}
                    step={100}
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Rent per sq ft (₹)</Label>
                    <span className="text-sm font-mono">₹{proposalForm.rent_per_sqft}</span>
                  </div>
                  <Slider
                    value={[proposalForm.rent_per_sqft]}
                    onValueChange={([v]) => setProposalForm(f => ({ ...f, rent_per_sqft: v }))}
                    min={100}
                    max={1000}
                    step={10}
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>CAM per sq ft (₹)</Label>
                    <span className="text-sm font-mono">₹{proposalForm.cam_per_sqft}</span>
                  </div>
                  <Slider
                    value={[proposalForm.cam_per_sqft]}
                    onValueChange={([v]) => setProposalForm(f => ({ ...f, cam_per_sqft: v }))}
                    min={20}
                    max={150}
                    step={5}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Lease Term (years)</Label>
                    <Select 
                      value={proposalForm.lease_term_years.toString()} 
                      onValueChange={(v) => setProposalForm(f => ({ ...f, lease_term_years: parseInt(v) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[3, 5, 7, 9, 10, 15].map(y => (
                          <SelectItem key={y} value={y.toString()}>{y} years</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Escalation (%)</Label>
                    <Select 
                      value={proposalForm.escalation_percent.toString()} 
                      onValueChange={(v) => setProposalForm(f => ({ ...f, escalation_percent: parseFloat(v) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[3, 4, 5, 6, 7, 8, 10, 12, 15].map(p => (
                          <SelectItem key={p} value={p.toString()}>{p}% annually</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Security Deposit</Label>
                    <Select 
                      value={proposalForm.security_deposit_months.toString()} 
                      onValueChange={(v) => setProposalForm(f => ({ ...f, security_deposit_months: parseInt(v) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[3, 6, 9, 12].map(m => (
                          <SelectItem key={m} value={m.toString()}>{m} months</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Revenue Share (%)</Label>
                    <Select 
                      value={proposalForm.revenue_share_percent.toString()} 
                      onValueChange={(v) => setProposalForm(f => ({ ...f, revenue_share_percent: parseFloat(v) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 2, 3, 5, 7, 10, 12, 15].map(p => (
                          <SelectItem key={p} value={p.toString()}>{p === 0 ? 'None' : `${p}%`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Right Column - Preview */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground">LIVE ROI PREVIEW</h3>
                
                {previewCalc && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <Card className="bg-primary/5 border-primary/20">
                        <CardContent className="p-4">
                          <p className="text-xs text-muted-foreground">Monthly Rent</p>
                          <p className="text-xl font-bold text-primary">{formatCurrency(previewCalc.monthlyRent)}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-emerald-500/5 border-emerald-500/20">
                        <CardContent className="p-4">
                          <p className="text-xs text-muted-foreground">Total Contract Value</p>
                          <p className="text-xl font-bold text-emerald-500">{formatCurrency(previewCalc.totalContractValue)}</p>
                        </CardContent>
                      </Card>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 bg-muted/30 rounded-lg">
                        <p className="text-xs text-muted-foreground">Annual Rent (Y1)</p>
                        <p className="font-semibold">{formatCurrency(previewCalc.annualRent)}</p>
                      </div>
                      <div className="p-2 bg-muted/30 rounded-lg">
                        <p className="text-xs text-muted-foreground">Security Deposit</p>
                        <p className="font-semibold">{formatCurrency(previewCalc.securityDeposit)}</p>
                      </div>
                      <div className="p-2 bg-muted/30 rounded-lg">
                        <p className="text-xs text-muted-foreground">Avg Annual</p>
                        <p className="font-semibold">{formatCurrency(previewCalc.avgAnnualRevenue)}</p>
                      </div>
                    </div>

                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={previewCalc.projections}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))', 
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                            formatter={(value) => [formatCurrency(value)]}
                          />
                          <Legend />
                          <Bar dataKey="rent" name="Base Rent" fill="hsl(var(--primary))" stackId="a" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="cam" name="CAM" fill="hsl(var(--chart-2))" stackId="a" radius={[0, 0, 0, 0]} />
                          {proposalForm.revenue_share_percent > 0 && (
                            <Bar dataKey="revShare" name="Rev Share" fill="hsl(var(--chart-3))" stackId="a" radius={[4, 4, 0, 0]} />
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    <p className="text-xs text-muted-foreground text-center">
                      {proposalForm.lease_term_years}-year projection with {proposalForm.escalation_percent}% annual escalation
                    </p>
                  </>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setNewProposalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateProposal} disabled={submitting} data-testid="submit-proposal-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Proposal
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Proposals</p>
                <p className="text-2xl font-bold mt-1">{proposals.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pipeline Value</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(totalPipelineValue)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Accepted Value</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(acceptedValue)}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversion Rate</p>
                <p className="text-2xl font-bold mt-1">
                  {proposals.length > 0 ? ((proposals.filter(p => p.status === 'accepted').length / proposals.length) * 100).toFixed(0) : 0}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Percent className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by brand or contact..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-proposals-input"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="negotiating">Negotiating</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Proposals List */}
      {filteredProposals.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Calculator className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Proposals Found</h3>
            <p className="text-muted-foreground mb-4">
              Create your first proposal to get started
            </p>
            <Button onClick={() => setNewProposalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Proposal
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProposals.map(proposal => (
            <Card 
              key={proposal.id} 
              className="hover:border-primary/30 transition-colors cursor-pointer"
              onClick={() => {
                setSelectedProposal(proposal);
                setViewProposalOpen(true);
              }}
              data-testid={`proposal-card-${proposal.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold">{proposal.brand_name}</h4>
                    <p className="text-sm text-muted-foreground">{proposal.contact_name}</p>
                  </div>
                  <Badge className={`${STATUS_COLORS[proposal.status]}`}>
                    {proposal.status}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div>
                    <p className="text-muted-foreground">Area</p>
                    <p className="font-medium">{proposal.carpet_area_sqft?.toLocaleString()} sq ft</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Monthly Rent</p>
                    <p className="font-medium">{formatCurrency(proposal.monthly_rent)}</p>
                  </div>
                </div>
                
                <div className="p-3 bg-primary/5 rounded-lg mb-3">
                  <p className="text-xs text-muted-foreground">Total Contract Value</p>
                  <p className="text-lg font-bold text-primary">
                    {formatCurrency(proposal.roi_metrics?.total_contract_value)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {proposal.lease_term_years} years • {proposal.escalation_percent}% escalation
                  </p>
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(proposal.created_at).toLocaleDateString()}
                  </span>
                  <div className="flex gap-1">
                    {proposal.status === 'draft' && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSendProposal(proposal.id);
                        }}
                      >
                        <Send className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Proposal Dialog */}
      <Dialog open={viewProposalOpen} onOpenChange={setViewProposalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedProposal?.brand_name} - Proposal</DialogTitle>
            <DialogDescription>ROI projection and details</DialogDescription>
          </DialogHeader>
          
          {selectedProposal && (
            <Tabs defaultValue="summary" className="py-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="projections">Projections</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>
              
              <TabsContent value="summary" className="space-y-4 mt-4">
                <div className="flex items-center gap-2 mb-4">
                  <Badge className={`${STATUS_COLORS[selectedProposal.status]}`}>
                    {selectedProposal.status}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Created {new Date(selectedProposal.created_at).toLocaleDateString()}
                  </span>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-primary/5">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-muted-foreground">Monthly Rent</p>
                      <p className="text-xl font-bold">{formatCurrency(selectedProposal.monthly_rent)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-emerald-500/5">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-muted-foreground">Total Contract</p>
                      <p className="text-xl font-bold">{formatCurrency(selectedProposal.roi_metrics?.total_contract_value)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-500/5">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-muted-foreground">Security Deposit</p>
                      <p className="text-xl font-bold">{formatCurrency(selectedProposal.security_deposit)}</p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Contact</p>
                    <p className="font-medium">{selectedProposal.contact_name}</p>
                    <p className="text-muted-foreground">{selectedProposal.contact_email}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Terms</p>
                    <p className="font-medium">{selectedProposal.lease_term_years} years</p>
                    <p className="text-muted-foreground">{selectedProposal.escalation_percent}% annual escalation</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Area</p>
                    <p className="font-medium">{selectedProposal.carpet_area_sqft?.toLocaleString()} sq ft</p>
                    <p className="text-muted-foreground">₹{selectedProposal.rent_per_sqft}/sq ft</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Additional Charges</p>
                    <p className="font-medium">CAM: ₹{selectedProposal.cam_per_sqft}/sq ft</p>
                    <p className="text-muted-foreground">Rev Share: {selectedProposal.revenue_share_percent}%</p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="projections" className="mt-4">
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={selectedProposal.projections}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" />
                      <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [formatCurrency(value)]}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="base_rent" name="Base Rent" stroke="hsl(var(--primary))" strokeWidth={2} />
                      <Line type="monotone" dataKey="total" name="Total" stroke="hsl(var(--chart-2))" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Year</th>
                        <th className="text-right p-2">Base Rent</th>
                        <th className="text-right p-2">CAM</th>
                        <th className="text-right p-2">Rev Share</th>
                        <th className="text-right p-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProposal.projections?.map((p, i) => (
                        <tr key={i} className="border-b">
                          <td className="p-2">Year {p.year}</td>
                          <td className="text-right p-2">{formatCurrency(p.base_rent)}</td>
                          <td className="text-right p-2">{formatCurrency(p.cam_charges)}</td>
                          <td className="text-right p-2">{formatCurrency(p.revenue_share)}</td>
                          <td className="text-right p-2 font-medium">{formatCurrency(p.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
              
              <TabsContent value="history" className="mt-4">
                <div className="space-y-3">
                  {selectedProposal.versions?.map((v, i) => (
                    <div key={i} className="flex gap-3 text-sm">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        {v.version}
                      </div>
                      <div>
                        <p className="font-medium">{v.changes}</p>
                        <p className="text-xs text-muted-foreground">
                          {v.changed_by} • {new Date(v.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <div className="flex gap-2">
              {selectedProposal?.status === 'draft' && (
                <Button variant="outline" onClick={() => handleSendProposal(selectedProposal.id)}>
                  <Send className="w-4 h-4 mr-2" />
                  Send Proposal
                </Button>
              )}
              <Select 
                value={selectedProposal?.status}
                onValueChange={(v) => handleUpdateStatus(selectedProposal.id, v)}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Update status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="negotiating">Negotiating</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
